import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import i18next from 'i18next'

import { error, success } from 'src/components/system_wide/notification'

import axios from 'src/lib/axios'

const { t } = i18next

const initialState = {
  total: 0,
  messages: [],
  show_form: false,
  scroll_to_message_id: null,
  current_message: {
    id: null,
    title: '',
    message: '',
    created_at: null,
  },
}

export const getMessages = createAsyncThunk('messages/list', async ({ offset, limit, sortData, filter }, thunkApi) => {
  try {
    let { total: currentTotal } = thunkApi.getState().messages
    let { id: logged_user_id } = thunkApi.getState().userInfo
    if (offset > currentTotal) return

    let { sort_column, sort_direction } = sortData ?? { sort_column: 'created_at', sort_direction: 'DESC' }
    let response = await axios({
      url: `/api/messages`,
      params: { offset, limit, sort_column, sort_direction, filter },
    })
    let { total, messages } = response.data
    return { total, messages, reset: offset === 0, logged_user_id }
  } catch (err) {
    console.log(err)
    error(t('store.messages.get_messages_list_error', 'Impossibile recuperare lista dei messaggi'))
    return thunkApi.rejectWithValue()
  }
})

export const createMessage = createAsyncThunk('messages/create', async ({ message }, thunkApi) => {
  try {
    await axios({
      url: `/api/message`,
      method: 'post',
      data: { ...message },
    })
    success(t('store.messages.create_message_success', 'Messaggio creato con successo'))
    thunkApi.dispatch(toggleShowForm())
  } catch (err) {
    error(t('store.messages.create_message_error', 'Impossibile creare il messaggio'))
    thunkApi.dispatch(toggleShowForm())
    return thunkApi.rejectWithValue()
  }
})

export const updateMessage = createAsyncThunk('messages/update', async ({ message }, thunkApi) => {
  try {
    await axios({
      url: `/api/message/${message.id}`,
      method: 'patch',
      data: { ...message },
    })
    success(t('store.messages.update_message_success', 'Messaggio aggiornato con successo'))
    thunkApi.dispatch(toggleShowForm())
  } catch (err) {
    error(t('store.messages.update_message_error', 'Impossibile aggiornare il messaggio'))
    thunkApi.dispatch(toggleShowForm())
    return thunkApi.rejectWithValue()
  }
})

export const markMessages = createAsyncThunk(
  'messages/markMessages',
  async ({ message_id, mark, all = false }, thunkApi) => {
    try {
      await axios({
        url: `/api/message/mark_messages`,
        method: 'patch',
        data: { message_id, mark, all },
      })
    } catch (err) {
      return thunkApi.rejectWithValue()
    }
  }
)

export const deleteMessage = createAsyncThunk('messages/delete', async (id, thunkApi) => {
  try {
    await axios({ url: `/api/message/${id}`, method: 'delete' })
    success(t('store.messages.delete_message_success', 'Messaggio eliminato con successo'))
  } catch (err) {
    error(t('store.messages.delete_message_error', 'Impossibile eliminare il messaggio'))
    return thunkApi.rejectWithValue()
  }
})

export const messagesSlice = createSlice({
  name: 'messages',
  initialState,

  reducers: {
    setCurrentMessage: (state, action) => {
      let { id, title, message, created_at } = action.payload
      state.current_message.id = id
      state.current_message.title = title
      state.current_message.message = message
      state.current_message.created_at = created_at
    },
    toggleShowForm: state => {
      state.show_form = !state.show_form
    },

    scrollToMessage: (state, action) => {
      state.scroll_to_message_id = action.payload
    },
    resetScrollToMessage: state => {
      state.scroll_to_message_id = null
    },

    resetCurrentMessage: state => {
      state.current_message = initialState.current_message
    },

    resetMessages: () => initialState,
  },

  extraReducers: {
    [getMessages.fulfilled]: (state, action) => {
      if (!action?.payload) return
      let { total, logged_user_id } = action.payload
      let messages = (action.payload?.messages ?? []).map(m => ({
        ...m,
        read_at:
          m.created_by.id !== logged_user_id
            ? (m?.users ?? []).find(u => u.pivot.message_id === m.id && u.pivot.user_id === logged_user_id)?.pivot
                ?.read_at ?? null
            : m.created_at,
      }))
      state.total = total
      state.messages = action.payload.reset ? [...messages] : [...state.messages, ...messages]
    },
    [getMessages.rejected]: state => {
      state.total = initialState.total
      state.messages = initialState.messages
    },
  },
})

export const {
  setCurrentMessage,
  resetCurrentMessage,
  toggleShowForm,
  scrollToMessage,
  resetScrollToMessage,
  resetMessages,
} = messagesSlice.actions
export default messagesSlice.reducer
